/**
 * BlockCard — one block (בלוק) of the menu, e.g. "ארוחת בוקר". The only
 * elevated surface in the editor tree: sub-blocks and ingredients sit inside
 * it and use indent + rail + tint for depth instead of nesting more cards.
 *
 * Holds the block name, the coach's per-block comment, the reorder / template
 * / delete actions, and the sub-block list with its placement drop zones.
 *
 * Vocabulary: the coach sees בלוק / תת-בלוק (doof's terms). The client never
 * sees either — an unnamed block renders on the client page with no heading,
 * which is why the name field carries an inline hint instead of an error.
 */

import {
  BookmarkSimple,
  CaretDown,
  CaretUp,
  ChatText,
  Info,
  Plus,
  Trash,
} from "@phosphor-icons/react";

import { calcBlockNutrition } from "../../../utils/nutritionCalc";
import HoverTip from "../../ui/HoverTip";
import MacroSummaryStrip from "./MacroSummaryStrip";
import NmbIconButton from "./NmbIconButton";
import SubBlockCard from "./SubBlockCard";
import "./MenuCards.css";

/** Blocks cycle through four accent rails (see MenuCards.css). */
const ACCENT_COUNT = 4;

const EMPTY_NAME_TIP = "בלי שם, הלקוח/ה יראה/תראה את הפריטים של הבלוק הזה בלי כותרת";

const BlockCard = ({ block, index, count, ctx }) => {
  const cardKey = `block:${block.blockId}`;
  const error = ctx.errors[cardKey];
  const isFlashing = ctx.flashKey === cardKey;
  const subBlocks = block.subBlocks || [];
  const totals = calcBlockNutrition(block, ctx.isAveragesMenu);
  const isCommentOpen = !!ctx.openComments[block.blockId];
  const isUnnamed = !(block.blockName || "").trim();

  const dropZone = (position) =>
    ctx.canDrop("subBlock") ? (
      <button
        type="button"
        className="nmb-dropzone subblock"
        onClick={() =>
          ctx.onDropHere({ level: "subBlock", blockIndex: index, index: position })
        }
      >
        הוסף כאן
      </button>
    ) : null;

  return (
    <div
      className={`nmb-block ${isFlashing ? "flash" : ""} ${error ? "invalid" : ""}`}
      data-nmb-key={cardKey}
      data-accent={index % ACCENT_COUNT}
    >
      <div className="nmb-block-head">
        <HoverTip text={isUnnamed ? EMPTY_NAME_TIP : ""} className="nmb-name-tip" placement="bottom">
          <input
            type="text"
            className={`nmb-block-name ${isUnnamed ? "is-empty" : ""}`}
            value={block.blockName || ""}
            onChange={(event) =>
              ctx.onBlockChange(index, { blockName: event.target.value })
            }
            placeholder="שם הבלוק (לדוגמה: ארוחת בוקר)"
            aria-label={`שם הבלוק ${index + 1}`}
          />
        </HoverTip>

        <div className="nmb-block-actions">
          <NmbIconButton
            label="העלאת הבלוק למעלה"
            disabled={index === 0}
            onClick={() => ctx.onMoveBlock(index, -1)}
          >
            <CaretUp size={15} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="הורדת הבלוק למטה"
            disabled={index === count - 1}
            onClick={() => ctx.onMoveBlock(index, 1)}
          >
            <CaretDown size={15} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="שמירת הבלוק כתבנית"
            variant="alt"
            onClick={() => ctx.onSaveBlockTemplate(index)}
          >
            <BookmarkSimple size={15} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="מחיקת הבלוק"
            variant="danger"
            onClick={() => ctx.onDeleteBlock(index)}
          >
            <Trash size={15} weight="fill" />
          </NmbIconButton>
        </div>
      </div>

      {isUnnamed && (
        <p className="nmb-name-hint">
          <Info size={12} weight="fill" />
          בלי שם, הלקוח/ה יראה/תראה את הפריטים של הבלוק הזה ללא כותרת.
        </p>
      )}

      {error && <p className="nmb-card-error">{error}</p>}

      <MacroSummaryStrip
        totals={totals}
        label={ctx.isAveragesMenu ? "ממוצע ערכים לבלוק" : "סיכום ערכים לבלוק"}
        tip={
          ctx.isAveragesMenu
            ? "בתפריט ממוצעים התת-בלוקים הם אפשרויות חלופיות, ולכן זהו הממוצע של התת-בלוקים בבלוק — לא הסכום"
            : "סכום הערכים של כל התת-בלוקים בבלוק הזה (פריט עם חלופות נספר לפי הממוצע של כל החלופות שלו)"
        }
      />

      <div className="nmb-block-body">
        {dropZone(0)}

        {subBlocks.map((subBlock, subIndex) => (
          <div key={subBlock.subBlockId || `sub-${subIndex}`}>
            <SubBlockCard
              subBlock={subBlock}
              blockIndex={index}
              index={subIndex}
              count={subBlocks.length}
              ctx={ctx}
            />
            {dropZone(subIndex + 1)}
          </div>
        ))}
      </div>

      <div className="nmb-block-footer">
        <button
          type="button"
          className="nmb-add-btn"
          onClick={() => ctx.onAddSubBlock(index)}
        >
          <Plus size={14} weight="bold" />
          <span>הוספת תת-בלוק</span>
        </button>

        <button
          type="button"
          className={`nmb-comment-btn ${block.comment ? "filled" : ""}`}
          aria-expanded={isCommentOpen}
          onClick={() => ctx.onToggleComment(block.blockId)}
        >
          <ChatText size={13} weight="fill" />
          <span>{block.comment ? "עריכת הערות לבלוק" : "הוספת הערות לבלוק"}</span>
        </button>
      </div>

      <div className={`nmb-comment-wrap ${isCommentOpen ? "open" : ""}`}>
        <div className="nmb-comment-inner">
          <textarea
            className="nmb-textarea"
            rows={2}
            value={block.comment || ""}
            onChange={(event) =>
              ctx.onBlockChange(index, { comment: event.target.value })
            }
            placeholder="הערה שתופיע ליד הבלוק בתפריט של הלקוח/ה"
            aria-label="הערות לבלוק"
          />
        </div>
      </div>
    </div>
  );
};

export default BlockCard;
